import { useEffect } from 'react';
import { HiSparkles, HiLocationMarker, HiShieldCheck, HiCheckCircle, HiStar, HiClock, HiExternalLink } from 'react-icons/hi';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Section, { SectionHeader } from '../components/ui/Section';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import siteContent from '../data/siteContent.json';
import testimonials from '../data/testimonials.json';
import heroPhoto from '../assets/images/hero-home-new.jpg';
import gabPhoto from '../assets/images/gab_profile_pic_new.png';
import homeBg from '../assets/images/bb-background.png';
import homeHeroBg from '../assets/images/home-hero-bg.png';
import Seo from '../components/Seo';

// Scroll animation setup
function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

export default function Home() {
  const allTestimonials = testimonials.testimonials;
  useScrollAnimation();

  return (
    <>
      <Seo path="/" />
      {/* Hero Section - Split Layout */}
      <ChalkboardBackground 
        backgroundImage={homeHeroBg}
        backgroundOpacity={0.6}
        overlayColor="bg-dusty-rose/[.07]"
        className="relative vignette min-h-[85vh] overflow-hidden"
      >
        <div className="container-custom py-12 lg:py-16 flex items-center min-h-[85vh]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left order-2 lg:order-1">
              <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-semibold text-chalk mb-2 uppercase tracking-wider">
                Bridal Hair & Makeup
              </h1>
              <p className="text-white italic font-medium mb-6">
                Serving Kansas City & Beyond
              </p>

              <div className="flex flex-col gap-4 items-center lg:items-start mb-8 w-full">
                <Button
                  href={siteContent.social.linktree}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                  size="lg"
                  className="!text-xs w-full sm:w-auto"
                >
                  Check Availability
                  <HiExternalLink className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  to="/about" 
                  variant="outline" 
                  size="lg"
                  className="!text-xs w-full sm:w-auto"
                >
                  Meet Your Team
                </Button>
              </div>

              {/* Urgency Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal-dark border border-dashed border-dusty-rose/50 text-dusty-rose rounded text-sm font-medium">
                <HiClock className="h-4 w-4" />
                2026 dates filling fast
              </div>
            </div>

            {/* Hero Image */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-sm w-full lg:max-w-md">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={heroPhoto}
                    alt="Blush & Bobby - Bridal Hair & Makeup"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Meet Gab Section */}
      <Section background="charcoal" className="border-t border-dusty-rose/10">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Gab's Photo */}
            <div className="order-1 flex justify-center">
              <div className="max-w-md w-full">
                <Card className="aspect-square p-2">
                  <img
                    src={gabPhoto}
                    alt="Gabrielle McKinzie"
                    className="w-full h-full object-cover"
                  />
                </Card>
              </div>
            </div>

            {/* Meet Gab Text */}
            <div className="order-2 text-center md:text-left">
              <h2 className="font-headline text-3xl uppercase tracking-wider mb-4 text-chalk">
                Gabrielle
              </h2>
              <p className="text-dusty-rose/80 leading-relaxed mb-6">
                {siteContent.about.story}
              </p>
              <Button to="/about" variant="outline" className="w-full sm:w-auto">
                Learn More About Our Team
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Trust Bar */}
      <ChalkboardBackground className="py-8 border-y border-dusty-rose/10">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-[10px] uppercase tracking-[0.2em] font-bold">
            <div className="flex items-center gap-3 text-dusty-rose/70 hover:text-dusty-rose transition-colors group">
              <HiStar className="h-4 w-4 text-dusty-rose group-hover:scale-110 transition-transform" />
              The Knot Best of Weddings
            </div>
            <div className="flex items-center gap-3 text-dusty-rose/70 hover:text-dusty-rose transition-colors group">
              <HiStar className="h-4 w-4 text-dusty-rose group-hover:scale-110 transition-transform" />
              Couples Choice Award
            </div>
            <div className="flex items-center gap-3 text-dusty-rose/70 hover:text-dusty-rose transition-colors group">
              <HiCheckCircle className="h-4 w-4 text-dusty-rose group-hover:scale-110 transition-transform" />
              Licensed & Insured
            </div>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Why Choose Us */}
      <ChalkboardBackground 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-20 md:py-28"
      >
        <div className="container-custom">
          <SectionHeader
            title="Why Brides Choose Us"
            dark={true}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="scroll-animate group">
              <Card className="p-8 md:p-10 h-full flex flex-col items-center text-center bg-black/50 border-white/10 rounded-2xl hover:bg-black/60 hover:border-white/20">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-dusty-rose/30 rounded-full mb-6 group-hover:bg-dusty-rose/10 transition-colors">
                  <HiSparkles className="h-8 w-8 text-dusty-rose" />
                </div>
                <h3 className="text-lg font-headline font-semibold mb-2 text-chalk uppercase tracking-wide group-hover:text-dusty-rose transition-colors">
                  Stress-Free Experience
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  I handle everything so you can relax and enjoy your day. Timeline planning and vendor coordination included.
                </p>
              </Card>
            </div>
            <div className="scroll-animate delay-100 group">
              <Card className="p-8 md:p-10 h-full flex flex-col items-center text-center bg-black/50 border-white/10 rounded-2xl hover:bg-black/60 hover:border-white/20">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-dusty-rose/30 rounded-full mb-6 group-hover:bg-dusty-rose/10 transition-colors">
                  <HiLocationMarker className="h-8 w-8 text-dusty-rose" />
                </div>
                <h3 className="text-lg font-headline font-semibold mb-2 text-chalk uppercase tracking-wide group-hover:text-dusty-rose transition-colors">
                  I Come to You
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  No salon visits needed. I bring everything to your venue, hotel, or home—wherever you're getting ready.
                </p>
              </Card>
            </div>
            <div className="scroll-animate delay-200 group">
              <Card className="p-8 md:p-10 h-full flex flex-col items-center text-center bg-black/50 border-white/10 rounded-2xl hover:bg-black/60 hover:border-white/20">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-dusty-rose/30 rounded-full mb-6 group-hover:bg-dusty-rose/10 transition-colors">
                  <HiShieldCheck className="h-8 w-8 text-dusty-rose" />
                </div>
                <h3 className="text-lg font-headline font-semibold mb-2 text-chalk uppercase tracking-wide group-hover:text-dusty-rose transition-colors">
                  Direct Communication
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  You get my personal cell number. Direct communication, no middlemen. I'm invested in your day.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Reviews */}
      <Section background="white" className="border-y border-dusty-rose/10">
        <SectionHeader
          title="What Brides Are Saying"
          subtitle="Real reviews from real brides"
          titleColor="text-dusty-rose"
        />
        <div className="scroll-animate">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allTestimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-cream p-6 rounded-2xl shadow-md"
              >
                <div className="mb-4">
                  <p className="font-semibold text-sm text-dusty-rose">{testimonial.name}</p>
                  <p className="text-xs text-gray-500">{testimonial.date}</p>
                </div>
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <HiStar key={i} className="h-4 w-4 text-dusty-rose" />
                  ))}
                </div>
                <p className="text-gray-600 text-sm">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button to="/reviews" variant="outline" className="text-dusty-rose">
              Read All Reviews
            </Button>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <ChalkboardBackground 
        as="section" 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Let's Create Your Dream Look
            </h2>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
}
